import type { FC } from 'react';
import React, { useState, useEffect } from 'react';

import classNames from 'clsx';
import { getDataAttributes } from '@wix/editor-elements-common-utils';

import type { ILoginSocialBarProps } from '../../LoginSocialBar.types';
import LoggedInSocialBar from './components/loggedInSocialBar';
import LoggedOffSocialBar from './components/loggedOffSocialBar';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from '../bi/events';

import styles from '../style/LoginSocialBar.module.scss';

const LoginSocialBar: FC<ILoginSocialBarProps> = props => {
  const {
    id,
    className,
    isLoggedIn,
    userName,
    loggedInTextState,
    loggedInText,
    customLoggedInText,
    useNativeMenu,
    menuItems,
    iconItems,
    currentPrimaryPageHref,
    reportBiOnClick,
    onLogin,
    onLogout,
    navigateTo,
    onMouseEnter,
    onMouseLeave,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    reportBiOnMenuItemClick,
    translations,
    avatarUri,
    reportBi,
    loggedInAvatarIcon,
    loggedOutAvatarIcon,
    loggedInCustomAvatar,
    loggedOutCustomAvatar,
    enableLoggingInStateInSSR,
    useNewMemberInitialsAvatar,
    onBlur,
  } = props;

  const [isHydrated, setIsHydrated] = useState(false);

  // only show logged in once hydration finishes for caching
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const onHover = (event: React.MouseEvent<Element, MouseEvent>) => {
    onMouseEnter?.(event);
    if (isLoggedIn && !isMenuOpen) {
      void reportBi(getMembersAreaNavigationEvent({ action: BIAction.Hover }), {
        endpoint: MEMBERS_APP_UOU_ENDPOINT,
      });
    }
  };

  const openMenu = () => {
    onMenuOpen();
    if (isLoggedIn) {
      void reportBi(
        getMembersAreaNavigationEvent({ action: BIAction.MenuClick }),
        {
          endpoint: MEMBERS_APP_UOU_ENDPOINT,
        },
      );
    }
  };

  const shouldShowLoggedIn = enableLoggingInStateInSSR
    ? isHydrated && isLoggedIn
    : isLoggedIn;

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(className, styles.root)}
      tabIndex={-1}
      onBlur={isLoggedIn && onBlur ? onBlur : undefined}
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeave}
    >
      {shouldShowLoggedIn ? (
        <LoggedInSocialBar
          avatarUri={avatarUri}
          isLoggedIn={isLoggedIn}
          userName={userName}
          id={id}
          loggedInTextState={loggedInTextState}
          loggedInText={loggedInText}
          customLoggedInText={customLoggedInText}
          currentPrimaryPageHref={currentPrimaryPageHref}
          menuItems={menuItems}
          iconItems={iconItems}
          renderIcons={!!iconItems.length}
          isMenuOpen={isMenuOpen}
          onMenuOpen={openMenu}
          reportBiOnMenuItemClick={reportBiOnMenuItemClick}
          navigateTo={navigateTo}
          onLogout={onLogout}
          onMenuClose={onMenuClose}
          avatarIcon={loggedInAvatarIcon}
          customAvatar={loggedInCustomAvatar}
          useNativeMenu={useNativeMenu}
          translations={translations}
          reportBi={reportBi}
          reportBiOnClick={reportBiOnClick}
          useNewMemberInitialsAvatar={useNewMemberInitialsAvatar}
        />
      ) : (
        <LoggedOffSocialBar
          id={id}
          avatarIcon={loggedOutAvatarIcon}
          customAvatar={loggedOutCustomAvatar}
          handleLogin={onLogin}
          reportBiOnClick={reportBiOnClick}
          translations={translations}
          useNewMemberInitialsAvatar={useNewMemberInitialsAvatar}
        />
      )}
    </div>
  );
};

export default LoginSocialBar;
